import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { patientService, Patient, PatientSummaryItem } from '../services/patientService';
import { meetingService, Meeting } from '../services/meetingService';
import { userService, User } from '../services/userService';
import { fileService, File } from '../services/fileService';
import fileTypeService, { FileType } from '../services/fileTypeService';
import { yomanService } from '../services/yomanService';
import { signatureRxService, PrescriptionItem } from '../services/signatureRxService';
import { ViewFilesModal } from '../components/modals/ViewFilesModal';
import { UploadFileModal } from '../components/modals/UploadFileModal';
import { EditFileModal } from '../components/modals/EditFileModal';
import { ViewQuestionnaireModal } from '../components/modals/ViewQuestionnaireModal';
import { ViewMeetingsModal, MeetingAction } from '../components/modals/ViewMeetingsModal';
import { EditCommentsModal } from '../components/modals/EditCommentsModal';
import { NoShowModal } from '../components/modals/NoShowModal';
import { CancelMeetingModal } from '../components/modals/CancelMeetingModal';
import { FinishTreatmentModal } from '../components/modals/FinishTreatmentModal';
import PatientIssuesBadge from '../components/PatientIssuesBadge';
import { UploadTranscriptModal } from '../components/modals/UploadTranscriptModal';
import { CMSummaryModal } from '../components/modals/CMSummaryModal';
import { DoctorSummaryModal } from '../components/modals/DoctorSummaryModal';
import { YomanSchedulingDialog } from '../components/dialogs/YomanSchedulingDialog';
import { PatientFormDialog } from '../components/dialogs/PatientFormDialog';
import EmailSendDialog from '../components/dialogs/EmailSendDialog';
import { communicationService, Communication, CommChannel, CommDirection } from '../services/communicationService';
import CommunicationsPanel from '../components/communications/CommunicationsPanel';
import CommunicationDetailModal from '../components/communications/CommunicationDetailModal';
import ComposeEmailDialog from '../components/communications/ComposeEmailDialog';
import { getApiError } from '../utils/apiError';
import SmsSendDialog from '../components/dialogs/SmsSendDialog';
import CallPatientDialog from '../components/dialogs/CallPatientDialog';
import { questionnaireService } from '../services/questionnaireService';
import { PatientQuestionnaire } from '../types/questionnaire.types';
import { activityLogService, ActivityLog } from '../services/activityLogService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { clinicService, Clinic } from '../services/clinicService';
import { MeetingDetailPanel } from '../components/meeting/MeetingDetailPanel';
import { meetingTextService } from '../services/meetingTextService';
import { formatDateTime, formatDate, formatTime, parseDateAsUTC, calculateAge } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { usePatientStatusConfigs } from '../hooks/usePatientStatusConfigs';
import { usePatientFieldConfigs } from '../hooks/usePatientFieldConfigs';
import NotesList from '../components/notes/NotesList';
import InternalTaskModal from '../components/internalTasks/InternalTaskModal';
import { internalTaskService, InternalTask } from '../services/internalTaskService';

/**
 * Render a SignatureRx date as DD/MM/YYYY.
 *
 * SignatureRx is inconsistent: some resources return ISO timestamps
 * ("2026-08-27T05:33:13.000000Z") and others a DD-MM-YYYY string ("28-04-2026 11:20"),
 * so handle both and fall back to showing the raw value rather than a broken date.
 */
const formatPrescriptionDate = (value?: string | null): string => {
  if (!value) return '—';
  const dmy = value.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) return `${dmy[1]}/${dmy[2]}/${dmy[3]}`;
  const parsed = new Date(value);
  if (!isNaN(parsed.getTime())) {
    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${parsed.getFullYear()}`;
  }
  return value;
};

/**
 * Which summary viewer a meeting needs.
 *
 * DoctorSummaryModal renders the editable per-section layout from
 * meeting_type.summary_type.summary_sections. CM and titration summaries are a
 * single block of text instead — titration's is built by the backend from the
 * summary definition's pdf_layout rather than by an AI prompt, and its summary
 * type carries no sections — so they open the single-text viewer.
 */
const usesSingleTextSummary = (meeting: Meeting): boolean => {
  const type = meeting.meeting_type?.type;
  return type === 'case_manager_intake' || type === 'case_manager_followup'
    || type === 'titration_intake' || type === 'titration_followup';
};


const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const canDownloadPrompt = !!(user?.user_privilege?.prompt_development);
  const userTimezone = useUserTimezone();
  const { statusLabel } = usePatientStatusConfigs();

  // Patient data
  const [patient, setPatient] = useState<Patient | null>(null);
  const { fieldConfigs } = usePatientFieldConfigs(patient?.patient_type_id ?? undefined);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [fileTypes, setFileTypes] = useState<FileType[]>([]);
  const [questionnaires, setQuestionnaires] = useState<PatientQuestionnaire[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [summaries, setSummaries] = useState<PatientSummaryItem[]>([]);
  const [selectedSummaryKey, setSelectedSummaryKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Internal tasks (per-patient)
  const [patientTasks, setPatientTasks] = useState<InternalTask[]>([]);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [editTask, setEditTask] = useState<InternalTask | null>(null);

  // Layout state
  const [activeTab, setActiveTab] = useState<'about' | 'meeting' | 'files' | 'questionnaires' | 'patient-history' | 'prescription' | 'communications'>('about');

  // Communications tab state
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [commsLoading, setCommsLoading] = useState(false);
  const [commChannelFilter, setCommChannelFilter] = useState<'' | CommChannel>('');
  const [commDirectionFilter, setCommDirectionFilter] = useState<'' | CommDirection>('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [selectedComm, setSelectedComm] = useState<Communication | null>(null);

  // Prescription tab (SignatureRx) — titration meetings only
  const [prescriptions, setPrescriptions] = useState<PrescriptionItem[]>([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);
  const [prescriptionsError, setPrescriptionsError] = useState<string | null>(null);
  const [prescriptionsLinked, setPrescriptionsLinked] = useState<boolean>(false);
  const [openingSignatureRx, setOpeningSignatureRx] = useState(false);
  const loadPrescriptionsRef = useRef<(() => void) | null>(null);
  const [leftPaneWidth, setLeftPaneWidth] = useState(65); // percentage
  const [isDraggingPaneDivider, setIsDraggingPaneDivider] = useState(false);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('patient-page-right-panel-collapsed');
      return stored === 'true';
    } catch (error) {
      return false;
    }
  });

  // Meeting selection
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);

  // Meeting filters
  const [selectedTherapist, setSelectedTherapist] = useState<string>('');
  const [selectedMeetingType, setSelectedMeetingType] = useState<string>('');

  // Patient History table state
  const [patientHistorySortColumn, setPatientHistorySortColumn] = useState<string>('created_at');
  const [patientHistorySortDirection, setPatientHistorySortDirection] = useState<'asc' | 'desc'>('desc');
  const [patientHistoryColumnWidths, setPatientHistoryColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem('patient-page-history-table-widths');
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });
  const [patientHistoryResizingColumn, setPatientHistoryResizingColumn] = useState<string | null>(null);
  const [patientHistoryStartX, setPatientHistoryStartX] = useState(0);
  const [patientHistoryStartWidth, setPatientHistoryStartWidth] = useState(0);

  // Modal states
  const [isViewFilesModalOpen, setIsViewFilesModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [isEditFileModalOpen, setIsEditFileModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isViewQuestionnaireModalOpen, setIsViewQuestionnaireModalOpen] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<PatientQuestionnaire | null>(null);
  const [isViewMeetingsModalOpen, setIsViewMeetingsModalOpen] = useState(false);
  const [isEditNotesModalOpen, setIsEditNotesModalOpen] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isUploadTranscriptModalOpen, setIsUploadTranscriptModalOpen] = useState(false);
  const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [clinicState, setClinicState] = useState<string | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [defaultCountryCode, setDefaultCountryCode] = useState('+44');
  const [patientFieldErrors, setPatientFieldErrors] = useState<{ id_number?: string }>({});
  const [isYomanDialogOpen, setIsYomanDialogOpen] = useState(false);
  const [yomanLoading, setYomanLoading] = useState(false);

  // Meeting action modals (from ViewMeetingsModal)
  const [selectedMeetingForAction, setSelectedMeetingForAction] = useState<Meeting | null>(null);
  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
  const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
  const [isEditMeetingCommentsModalOpen, setIsEditMeetingCommentsModalOpen] = useState(false);
  const [isMeetingEmailDialogOpen, setIsMeetingEmailDialogOpen] = useState(false);
  const [isMeetingSmsDialogOpen, setIsMeetingSmsDialogOpen] = useState(false);
  // A call started from a meeting row joins that meeting's conference; the header
  // button starts a patient-level call with no meeting attached.
  const [isMeetingCallDialogOpen, setIsMeetingCallDialogOpen] = useState(false);
  const [isPatientCallDialogOpen, setIsPatientCallDialogOpen] = useState(false);
  const [meetingsRefreshKey, setMeetingsRefreshKey] = useState(0);
  const [isCMSummaryModalOpen, setIsCMSummaryModalOpen] = useState(false);
  const [cmSummaryMeeting, setCmSummaryMeeting] = useState<Meeting | null>(null);
  const [isDoctorSummaryModalOpen, setIsDoctorSummaryModalOpen] = useState(false);
  const [doctorSummaryMeeting, setDoctorSummaryMeeting] = useState<Meeting | null>(null);

  // Refs
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMountRef = useRef(true);
  const meetingIdFromUrlRef = useRef<number | null>(null);

  // Form setup for editing patient
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, submitCount },
    reset,
    control,
  } = useForm({ mode: 'onChange' });

  // Notes handlers
  const handleSaveNotes = async (newNotes: string) => {
    if (!patient) return;
    setIsSavingNotes(true);
    try {
      await patientService.updatePatient(patient.id, { notes: newNotes });
      // Refresh patient data
      const updatedPatient = await patientService.getPatient(patient.id);
      setPatient(updatedPatient);
      setIsEditNotesModalOpen(false);
    } catch (err) {
      console.error('Failed to update notes:', err);
      throw new Error('Failed to update notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Patient edit handlers
  const handleEditPatient = () => {
    if (patient) {
      reset({
        first_name: patient.first_name,
        last_name: patient.last_name,
        email: patient.email || '',
        phone: patient.phone || '',
        date_of_birth: patient.date_of_birth,
        start_medication: patient.start_medication || '',
        id_number: patient.id_number || '',
        gender: patient.gender || '',
        additional_fields: patient.additional_fields || {},
      });
      setIsEditPatientModalOpen(true);
    }
  };

  const handleSavePatient = async (data: any) => {
    if (!patient) return;

    try {
      const payload = { ...data, start_medication: data.start_medication || null };
      await patientService.updatePatient(patient.id, payload);
      // Refresh patient data
      const updatedPatient = await patientService.getPatient(patient.id);
      setPatient(updatedPatient);
      setIsEditPatientModalOpen(false);
      toast.success('Patient updated successfully');
    } catch (err: any) {
      console.error('Failed to update patient:', err);
      const detail = err.response?.data?.detail;
      const fullError = JSON.stringify(err.response?.data || '').toLowerCase();
      if (err.response?.status === 400 && (fullError.includes('id number') || fullError.includes('id_number'))) {
        setPatientFieldErrors({ id_number: 'ID Number / NHS Number already exists' });
        toast.error('ID Number / NHS Number already exists');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to update patient');
      }
    }
  };

  // Transcript upload handler
  const handleUploadTranscript = async (meetingId: number, file: globalThis.File) => {
    setIsUploadingTranscript(true);
    try {
      await meetingTextService.uploadTranscript(meetingId, file);
      setIsUploadTranscriptModalOpen(false);
      // Optionally refresh meetings or show success message
    } catch (err) {
      console.error('Failed to upload transcript:', err);
      throw new Error('Failed to upload transcript');
    } finally {
      setIsUploadingTranscript(false);
    }
  };

  // Yoman handlers
  const handleYomanCreateInvite = async (meetingTypeId: number, therapistId?: number) => {
    if (!patient) {
      return;
    }

    try {
      setYomanLoading(true);

      const response = await yomanService.createInvite({
        patient_id: patient.id,
        meeting_type_id: meetingTypeId,
        therapist_id: therapistId,
      });

      // Open in new tab
      window.open(response.invite_url, '_blank', 'noopener,noreferrer');

      toast.success('Yoman meeting invite created');
      setIsYomanDialogOpen(false);
    } catch (error) {
      console.error('Error creating Yoman invite:', error);
      toast.error('Failed to create Yoman meeting invite');
      throw error;
    } finally {
      setYomanLoading(false);
    }
  };

  const handleYomanDialogClose = () => {
    setIsYomanDialogOpen(false);
    setYomanLoading(false);
  };

  // File handlers
  const handleUploadFile = async (file: globalThis.File, description: string, fileTypeId?: number) => {
    if (!patient) return;
    try {
      await fileService.uploadFile(file, description, patient.id, undefined, undefined, undefined, fileTypeId);
      // Refresh files list
      const filesResponse = await fileService.getFiles(patient.id);
      setFiles(filesResponse.items);
      setIsUploadFileModalOpen(false);
    } catch (err) {
      console.error('Failed to upload file:', err);
      alert('Failed to upload file');
    }
  };


  const handleMeetingAction = (meeting: Meeting, action: MeetingAction) => {
    setSelectedMeetingForAction(meeting);
    if (action === 'view') {
      navigate(`/meetings/${meeting.id}`);
    } else if (action === 'noshow') {
      setIsNoShowModalOpen(true);
    } else if (action === 'cancel') {
      setIsCancelMeetingModalOpen(true);
    } else if (action === 'finish') {
      setIsFinishTreatmentModalOpen(true);
    } else if (action === 'comments') {
      setIsEditMeetingCommentsModalOpen(true);
    } else if (action === 'email') {
      setIsMeetingEmailDialogOpen(true);
    } else if (action === 'sms') {
      setIsMeetingSmsDialogOpen(true);
    } else if (action === 'call') {
      setIsMeetingCallDialogOpen(true);
    } else if (action === 'upload-transcript') {
      setIsUploadTranscriptModalOpen(true);
    }
  };

  const handleEditFile = (file: File) => {
    setSelectedFile(file);
    setIsEditFileModalOpen(true);
  };

  const handleSaveFileEdit = async (fileId: number, description: string, fileTypeId?: number) => {
    if (!patient) return;
    try {
      // Update file via API
      await fileService.updateFile(fileId, description, fileTypeId);
      // Refresh files list
      const filesResponse = await fileService.getFiles(patient.id);
      setFiles(filesResponse.items);
      // Close modal
      setIsEditFileModalOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error('Failed to update file:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const handleDownloadFile = async (fileId: number) => {
    try {
      await fileService.downloadFile(fileId);
    } catch (err) {
      console.error('Failed to download file:', err);
      alert('Failed to download file');
    }
  };

  // Save right panel collapse state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('patient-page-right-panel-collapsed', String(isRightPanelCollapsed));
    } catch (error) {
      console.error('Failed to save right panel collapsed state:', error);
    }
  }, [isRightPanelCollapsed]);

  // Save history table column widths to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('patient-page-history-table-widths', JSON.stringify(patientHistoryColumnWidths));
    } catch (error) {
      console.error('Failed to save history table widths:', error);
    }
  }, [patientHistoryColumnWidths]);

  const handleOpenSignatureRx = useCallback(async () => {
    if (!patient) return;
    setOpeningSignatureRx(true);
    try {
      const res = await signatureRxService.getPrescribeLink(patient.id);
      window.open(res.url, '_blank', 'noopener,noreferrer');
      if (res.created) {
        toast.success('Patient created in SignatureRx');
        // The patient is linked now, so the list has somewhere to read from.
        loadPrescriptionsRef.current?.();
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const missing = detail?.missing_fields as { key: string; label: string }[] | undefined;
      if (missing && missing.length > 0) {
        toast.error(`Cannot open SignatureRx — missing: ${missing.map(f => f.label).join(', ')}`);
      } else {
        toast.error((typeof detail === 'string' && detail) || detail?.message || 'Failed to open SignatureRx');
      }
    } finally {
      setOpeningSignatureRx(false);
    }
  }, [patient]);

  const loadPrescriptions = useCallback(async () => {
    if (!patient) return;
    setPrescriptionsLoading(true);
    setPrescriptionsError(null);
    try {
      const res = await signatureRxService.getPrescriptions(patient.id);
      setPrescriptions(res.items || []);
      setPrescriptionsLinked(!!res.signaturerx_patient_id);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      setPrescriptionsError(
        (typeof detail === 'string' && detail) ||
          detail?.message ||
          'Could not load prescriptions from SignatureRx.'
      );
      setPrescriptions([]);
    } finally {
      setPrescriptionsLoading(false);
    }
  }, [patient]);

  useEffect(() => {
    loadPrescriptionsRef.current = loadPrescriptions;
  }, [loadPrescriptions]);

  // Pull fresh on every tab open — SignatureRx is the system of record, we cache nothing.
  useEffect(() => {
    if (activeTab === 'prescription') {
      loadPrescriptions();
    }
  }, [activeTab, loadPrescriptions]);

  const loadCommunications = useCallback(async () => {
    if (!id) return;
    setCommsLoading(true);
    try {
      const res = await communicationService.getPatientCommunications(Number(id), {
        channel: commChannelFilter || undefined,
        direction: commDirectionFilter || undefined,
        page_size: 100,
      });
      setCommunications(res.items);
    } catch (e) {
      toast.error(getApiError(e, 'Failed to load communications'));
    } finally {
      setCommsLoading(false);
    }
  }, [id, commChannelFilter, commDirectionFilter]);

  useEffect(() => {
    if (activeTab === 'communications') loadCommunications();
  }, [activeTab, loadCommunications]);



  // Handle pane divider drag
  useEffect(() => {
    const handlePaneMouseMove = (e: MouseEvent) => {
      if (!isDraggingPaneDivider || !mainContainerRef.current) return;

      const container = mainContainerRef.current;
      const containerWidth = container.clientWidth;
      const currentX = e.clientX - container.getBoundingClientRect().left;
      const newWidth = Math.max(30, Math.min(70, (currentX / containerWidth) * 100));
      setLeftPaneWidth(newWidth);
    };

    const handlePaneMouseUp = () => {
      setIsDraggingPaneDivider(false);
    };

    if (isDraggingPaneDivider) {
      document.addEventListener('mousemove', handlePaneMouseMove);
      document.addEventListener('mouseup', handlePaneMouseUp);
      return () => {
        document.removeEventListener('mousemove', handlePaneMouseMove);
        document.removeEventListener('mouseup', handlePaneMouseUp);
      };
    }
  }, [isDraggingPaneDivider]);

  // Read meeting_id from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const meetingIdParam = params.get('meeting_id');
    if (meetingIdParam) {
      meetingIdFromUrlRef.current = parseInt(meetingIdParam, 10);
    }
  }, []);

  // After meetings load, open the meeting from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const meetingIdParam = params.get('meeting_id');
    if (meetingIdParam && meetings.length > 0) {
      const meetingId = parseInt(meetingIdParam, 10);
      if (meetings.find(m => m.id === meetingId)) {
        setSelectedMeetingId(meetingId);
      }
    }
  }, [meetings]);

  // Sync meeting_id in URL whenever selectedMeetingId changes
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const currentParam = params.get('meeting_id');
    const currentValue = currentParam ? parseInt(currentParam, 10) : null;

    if (selectedMeetingId && currentValue !== selectedMeetingId) {
      params.set('meeting_id', selectedMeetingId.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } else if (!selectedMeetingId && currentParam && meetings.length > 0) {
      params.delete('meeting_id');
      const newSearch = params.toString();
      window.history.replaceState({}, '', newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
    }
  }, [selectedMeetingId, meetings]);

  // Handle history table column resize
  const handleHistoryResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();

    const element = (e.target as HTMLElement).closest('th');
    const actualWidth = element ? element.offsetWidth : currentWidth;

    setPatientHistoryResizingColumn(column);
    setPatientHistoryStartX(e.clientX);
    setPatientHistoryStartWidth(actualWidth);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!patientHistoryResizingColumn) return;

      const diff = e.clientX - patientHistoryStartX;
      const newWidth = Math.max(50, patientHistoryStartWidth + diff);

      setPatientHistoryColumnWidths(prev => ({
        ...prev,
        [patientHistoryResizingColumn]: newWidth
      }));
    };

    const handleMouseUp = () => {
      setPatientHistoryResizingColumn(null);
    };

    if (patientHistoryResizingColumn) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [patientHistoryResizingColumn, patientHistoryStartX, patientHistoryStartWidth]);

  // Load patient and meetings data
  useEffect(() => {
    const loadPatientData = async () => {
      if (!id || !currentTenant) return;

      try {
        setIsLoading(true);
        setError(null);

        const patientId = parseInt(id);
        // The patient record itself is required; everything else is best-effort
        // so a restricted secondary resource (e.g. activity logs for a user
        // without that privilege) doesn't blank the whole page.
        const patientData = await patientService.getPatient(patientId);
        setPatient(patientData);

        const [meetingsData, usersData, meetingTypesData, filesResponse, fileTypesData, questionnairesData, activityLogsResponse] = await Promise.all([
          meetingService.getMeetings(currentTenant.id, patientId).catch(() => []),
          userService.getUsers(currentTenant.id).catch(() => []),
          meetingTypeService.getMeetingTypes(currentTenant.id).catch(() => []),
          fileService.getFiles(patientId).catch(() => ({ items: [] })),
          fileTypeService.getFileTypes(currentTenant.id, 'PATIENT').catch(() => []),
          questionnaireService.getPatientQuestionnaires(currentTenant.id, patientId).catch(() => ({ items: [] })),
          activityLogService.getActivityLogs({ patient_id: patientId, page_size: 50 }).catch(() => ({ items: [] })),
        ]);

        setMeetings(meetingsData);
        setUsers(usersData);
        setMeetingTypes(meetingTypesData);
        setFiles(filesResponse.items);
        setFileTypes(fileTypesData);
        setQuestionnaires(questionnairesData.items);
        setActivityLogs(activityLogsResponse.items);
      } catch (err) {
        console.error('Error loading patient data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load patient data');
      } finally {
        setIsLoading(false);
      }
    };

    loadPatientData();
  }, [id, currentTenant]);

  // Load clinic state for patient field validation
  useEffect(() => {
    if (!currentTenant) return;
    clinicService.getClinics(currentTenant.id).then((fetchedClinics) => {
      setClinics(fetchedClinics);
      if (fetchedClinics.length > 0) {
        if (fetchedClinics[0].default_country_code) setDefaultCountryCode(fetchedClinics[0].default_country_code);
        setClinicState(fetchedClinics[0].state || null);
      }
    }).catch(() => {});
  }, [currentTenant]);

  // Load summaries independently so failures don't affect the main page load
  useEffect(() => {
    if (!id) return;
    const patientId = parseInt(id);
    patientService.getPatientSummaries(patientId)
      .then((data) => {
        const items = data || [];
        setSummaries(items);
        if (items.length > 0) {
          const first = items[0];
          const firstKey = first.meeting_id != null ? `meeting-${first.meeting_id}` : 'questionnaire';
          setSelectedSummaryKey(firstKey);
        }
      })
      .catch((err) => {
        console.error('Failed to load summaries:', err);
      });
  }, [id]);

  const loadPatientTasks = useCallback(() => {
    if (!id) return;
    internalTaskService.getInternalTasks({ patient_id: parseInt(id) })
      .then(setPatientTasks)
      .catch(() => setPatientTasks([]));
  }, [id]);

  useEffect(() => { loadPatientTasks(); }, [loadPatientTasks]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B7F8A] mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button
            onClick={() => navigate('/patients')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Patients
          </button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Patient not found</p>
          <button
            onClick={() => navigate('/patients')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Patients
          </button>
        </div>
      </div>
    );
  }

  // Find assigned doctor, case manager and nurse prescriber from users list
  const doctor = users.find(u => u.id === patient?.doctor_id);
  const caseManager = users.find(u => u.id === patient?.case_manager_id);
  const nursePrescriber = users.find(u => u.id === patient?.nurse_prescriber_id);

  return (
    <div className="flex flex-col bg-gray-50 -mx-6 -my-6" style={{ height: 'calc(100vh - 60px)' }}>
      {/* Header - matching clinicui style */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center px-0 py-2 mr-[100px]">
          {/* Left side - Psychiatrist and Case Manager */}
          <div className="flex items-center gap-6 ml-4">
            <div>
              <span className="text-xs font-normal text-gray-500 mr-2">Psychiatrist:</span>
              <span className="text-sm font-bold text-gray-900">
                {doctor?.full_name || "-"}
              </span>
            </div>
            <div>
              <span className="text-xs font-normal text-gray-500 mr-2">Case Manager:</span>
              <span className="text-sm font-bold text-gray-900">
                {caseManager?.full_name || "-"}
              </span>
            </div>
            {nursePrescriber && (
              <div>
                <span className="text-xs font-normal text-gray-500 mr-2">Nurse Prescriber:</span>
                <span className="text-sm font-bold text-gray-900">
                  {nursePrescriber.full_name}
                </span>
              </div>
            )}
          </div>

          {/* Right side - Patient Name and HM_ID on one line, bold with gap */}
          <div className="text-right flex items-center justify-end gap-3">
            <p className="text-base font-bold text-gray-900">
              {patient.hm_id || patient.id}<span className="mx-3">-</span>{patient.full_name}
            </p>
            {patient.phone && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">{patient.phone}</span>
                <button
                  onClick={() => setIsPatientCallDialogOpen(true)}
                  title={`Call: ${patient.phone}`}
                  className="p-2 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    if (patient.phone) {
                      navigator.clipboard.writeText(patient.phone);
                    }
                  }}
                  title={`Copy: ${patient.phone}`}
                  className="p-2 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Two Panes */}
      <div className="flex-1 flex flex-row gap-0 relative overflow-hidden px-0 pt-0" ref={mainContainerRef}>
        {/* LEFT SIDE - Meeting List / Details */}
        <div
          className={`bg-white flex flex-col h-full overflow-hidden relative ${isRightPanelCollapsed ? 'flex-[0_0_100%]' : ''
            }`}
          style={
            !isRightPanelCollapsed
              ? { flex: `0 0 max(${leftPaneWidth}%, 70px)`, minWidth: '70px' }
              : { flex: `0 0 max(97%, 30px)`, minWidth: '30px' }
          }
        >
          {selectedMeetingId ? (
            (() => {
              const selectedMeeting = meetings.find(m => m.id === selectedMeetingId);
              if (!selectedMeeting) return (
                <div className="p-4 text-gray-600">
                  Meeting not found.{' '}
                  <button onClick={() => setSelectedMeetingId(null)} className="text-blue-600 hover:text-blue-700">
                    Back
                  </button>
                </div>
              );
              return (
                <MeetingDetailPanel
                  meeting={selectedMeeting}
                  meetingTypes={meetingTypes}
                  patient={patient}
                  onClose={() => setSelectedMeetingId(null)}
                  onMeetingUpdated={() => {
                    if (currentTenant) meetingService.getMeetings(currentTenant.id, patient.id).then(setMeetings);
                    setMeetingsRefreshKey(k => k + 1);
                  }}
                  onNoShow={(m) => { setSelectedMeetingForAction(m); setIsNoShowModalOpen(true); }}
                  onFinishTreatment={(m) => { setSelectedMeetingForAction(m); setIsFinishTreatmentModalOpen(true); }}
                  onSendEmail={(m) => { setSelectedMeetingForAction(m); setIsMeetingEmailDialogOpen(true); }}
                  onViewSummary={(m) => {
                    if (usesSingleTextSummary(m)) { setCmSummaryMeeting(m); setIsCMSummaryModalOpen(true); }
                    else { setDoctorSummaryMeeting(m); setIsDoctorSummaryModalOpen(true); }
                  }}
                  onPsySummaryReady={(m) => { setDoctorSummaryMeeting(m); setIsDoctorSummaryModalOpen(true); }}
                  onCmSummaryReady={(m) => { setCmSummaryMeeting(m); setIsCMSummaryModalOpen(true); }}
                  patientSummaries={summaries}
                  refreshKey={meetingsRefreshKey}
                  medicationType={clinics.find(c => c.id === patient.clinic_id)?.service?.medication_type ?? null}
                  clinicLanguage={clinics.find(c => c.id === patient.clinic_id)?.language}
                />
              );
            })()
          ) : (
            <>
              {/* Left pane header - Patient Overview */}
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Patient Overview</h3>
                {summaries.length > 0 && (
                  <select
                    value={selectedSummaryKey}
                    onChange={(e) => setSelectedSummaryKey(e.target.value)}
                    className="w-56 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {summaries.map((item) => {
                      const key = item.meeting_id != null ? `meeting-${item.meeting_id}` : 'questionnaire';
                      const [year, month, day] = item.date.split('-');
                      const typePart = item.meeting_type_name
                        ? `${item.meeting_type_name}${item.meeting_index != null ? ` ${item.meeting_index}` : ''}`
                        : item.name;
                      const label = `${day}/${month}/${year} - ${typePart}`;
                      return <option key={key} value={key}>{label}</option>;
                    })}
                  </select>
                )}
              </div>

              {/* Left pane content - selected summary */}
              <div className="flex-1 overflow-y-auto p-6">
                {summaries.length === 0 ? (
                  <p className="text-gray-400 text-sm">No summaries available</p>
                ) : (() => {
                  const selected = summaries.find(item =>
                    (item.meeting_id != null ? `meeting-${item.meeting_id}` : 'questionnaire') === selectedSummaryKey
                  );
                  return selected ? (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" dir={clinics.find(c => c.id === patient?.clinic_id)?.language === 'he' ? 'rtl' : 'ltr'}>{selected.content}</div>
                  ) : null;
                })()}
              </div>
            </>
          )}
        </div>

        {/* Resizable Divider */}
        {!isRightPanelCollapsed && (
          <div
            className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0"
            onMouseDown={() => setIsDraggingPaneDivider(true)}
            title="Drag to resize panes"
          />
        )}

        {/* RIGHT SIDE - Patient Details with Icon Tabs */}
        <div
          className={`bg-white rounded-lg shadow relative transition-all duration-300 h-full ${isRightPanelCollapsed ? 'flex-[0_0_60px] max-w-[60px]' : 'flex-1 min-w-0'
            }`}
        >
          {/* Toggle Button */}
          <button
            onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
            className="absolute left-[-12px] top-6 w-6 h-6 rounded-full bg-white border-2 border-gray-300 cursor-pointer flex items-center justify-center text-gray-700 text-lg z-20 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
            title={isRightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {isRightPanelCollapsed ? '‹' : '›'}
          </button>

          {/* Right Pane Container */}
          <div className={`flex flex-col h-full w-full overflow-hidden ${isRightPanelCollapsed ? 'hidden' : ''}`}>
            {/* Header with Icon Tabs */}
            <div className="flex flex-col h-full w-full px-3 py-2 border-b border-gray-200">
              <div className="flex items-center justify-between gap-2">
                {/* Edit and Action Icons on the left */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleEditPatient}
                    className="p-1 rounded transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    title="Edit Patient"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setIsViewMeetingsModalOpen(true)}
                    className="p-1 rounded transition-colors text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                    title="View All Meetings"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCreateTaskOpen(true)}
                    className="p-1 rounded transition-colors text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="Create Task"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                  </button>
                </div>

                {/* Tab Icons in RTL order (right to left) */}
                <div className="flex items-center gap-2">
                  {/* Communications Tab */}
                  <button
                    onClick={() => setActiveTab('communications')}
                    className={`p-1 rounded transition-colors ${activeTab === 'communications'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="Communications"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </button>

                  {/* Prescription Tab */}
                  {(
                    <button
                      onClick={() => setActiveTab('prescription')}
                      className={`p-1 rounded transition-colors ${activeTab === 'prescription'
                          ? 'bg-gray-200 text-gray-900'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      title="Prescriptions"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                    </button>
                  )}


                  {/* Patient History Tab */}
                  <button
                    onClick={() => setActiveTab('patient-history')}
                    className={`p-1 rounded transition-colors ${activeTab === 'patient-history'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="Patient History"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>

                  {/* Questionnaires Tab */}
                  <button
                    onClick={() => setActiveTab('questionnaires')}
                    className={`p-1 rounded transition-colors ${activeTab === 'questionnaires'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="Questionnaires"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </button>

                  {/* Files Tab */}
                  <button
                    onClick={() => setActiveTab('files')}
                    className={`p-1 rounded transition-colors ${activeTab === 'files'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="Files"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </button>

                  {/* Meeting Tab */}
                  <button
                    onClick={() => setActiveTab('meeting')}
                    className={`p-1 rounded transition-colors ${activeTab === 'meeting'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="Meeting"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </button>

                  {/* About Tab */}
                  <button
                    onClick={() => setActiveTab('about')}
                    className={`p-1 rounded transition-colors ${activeTab === 'about'
                        ? 'bg-gray-200 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    title="About"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto px-3 py-4">
                {/* About Tab */}
                {activeTab === 'about' && (
                  <div className="space-y-3">
                    {/* Status and Issues Row */}
                    <div className="grid grid-cols-2 gap-0">
                      {/* Status Field */}
                      <div className="border border-gray-300 rounded-l px-3 py-1 bg-gray-50 flex items-center gap-2">
                        <label className="text-xs font-bold text-gray-700 shrink-0">Status:</label>
                        <p className="text-xs font-normal text-gray-900 flex-1">
                          {patient.status ? statusLabel(patient.status) : "-"}
                        </p>
                      </div>

                      {/* Issues Field */}
                      <div className="border border-gray-300 rounded-r px-3 py-1 bg-gray-50 flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-900">Issues</label>
                        <PatientIssuesBadge patientId={patient.id} />
                      </div>
                    </div>

                    {/* Patient Basic Information */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="pl-3 pr-4 py-3">
                        <div className="grid grid-cols-2 gap-3">
                          {/* Row 0: ID Number | Gender */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">{clinicState === 'IL' ? 'ID Number' : 'NHS Number'}:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.id_number || "-"}>
                              {patient.id_number || "-"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">Gender:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.gender || "-"}>
                              {patient.gender || "-"}
                            </p>
                          </div>

                          {/* Row 2: DOB | Age */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">DOB:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.date_of_birth ? formatDate(patient.date_of_birth) : "-"}>
                              {patient.date_of_birth ? formatDate(patient.date_of_birth) : "-"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">Age:</label>
                            <p className="text-xs font-bold text-gray-900 truncate">
                              {calculateAge(patient.date_of_birth) ?? "-"}
                            </p>
                          </div>

                          {/* Row 3: Start Medication */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">Start Medication:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.start_medication ? formatDate(patient.start_medication) : "-"}>
                              {patient.start_medication ? formatDate(patient.start_medication) : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="pl-3 pr-4 py-3">
                        <div className="grid grid-cols-2 gap-3">
                          {/* Row 1: Email | Phone */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">Email:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.email || "-"}>
                              {patient.email || "-"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs font-normal text-gray-500">Phone:</label>
                            <p className="text-xs font-bold text-gray-900 truncate" title={patient.phone || "-"}>
                              {patient.phone || "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information (Dynamic Fields) */}
                    {fieldConfigs.filter(c => c.display_in_overview).filter(c => {
                      if (c.display_condition?.field === 'status' && patient.status) {
                        return c.display_condition.values.includes(patient.status);
                      }
                      return true;
                    }).length > 0 && (
                      <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                        <div className="pl-3 pr-4 py-3">
                          <div className="grid grid-cols-2 gap-3">
                            {fieldConfigs.filter(c => c.display_in_overview).filter(c => {
                              if (c.display_condition?.field === 'status' && patient.status) {
                                return c.display_condition.values.includes(patient.status);
                              }
                              return true;
                            }).map((config) => {
                              const value = patient.additional_fields?.[config.key];
                              let displayValue = '-';
                              if (value !== null && value !== undefined) {
                                if (config.field_type === 'bool') {
                                  displayValue = value === true || value === 'true' ? 'Yes' : 'No';
                                } else if (config.field_type === 'date' && value) {
                                  displayValue = formatDate(value);
                                } else if (config.field_type === 'single_select' && config.options) {
                                  const opt = config.options.find(o => o.value === value);
                                  displayValue = opt ? opt.label : String(value);
                                } else {
                                  displayValue = String(value);
                                }
                              }
                              return (
                                <div key={config.key} className="flex items-center gap-1">
                                  <label className="text-xs font-normal text-gray-500">{config.label}:</label>
                                  <p className="text-xs font-bold text-gray-900 truncate" title={displayValue}>
                                    {displayValue}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                        <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
                      </div>
                      <div className="p-2">
                        <NotesList entityType="patient" entityId={patient.id} />
                      </div>
                    </div>

                    {/* Internal Tasks */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-300 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">Tasks</h3>
                      </div>
                      <div className="px-4 py-2 divide-y divide-gray-100">
                        {patientTasks.length === 0 ? (
                          <p className="text-sm text-gray-500 py-1">No tasks.</p>
                        ) : (
                          patientTasks.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => setEditTask(t)}
                              className="w-full text-left py-2 hover:bg-gray-50 flex items-center justify-between"
                            >
                              <span className="text-sm text-gray-900">
                                {t.task_type_name || 'Task'} — {t.description || ''}
                              </span>
                              <span className="text-xs text-gray-500">{t.status} · {t.priority}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>

                  </div>
                )}

                {/* Meeting Tab */}
                {activeTab === 'meeting' && (
                  <div>
                    {/* Meeting History Header */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="px-4 py-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">Meeting History</h2>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setIsYomanDialogOpen(true)}
                            className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                            title="New Meeting"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <svg className="w-3 h-3 text-gray-600 absolute top-0 right-0 bg-white rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedMeetingForAction(null);
                              setIsUploadTranscriptModalOpen(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                            title="Upload Transcript"
                          >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <svg className="w-3 h-3 text-gray-600 absolute top-0 right-0 bg-white rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Filters Row */}
                      <div className="px-4 pb-3 flex items-center gap-3">
                        {/* Therapist Filter */}
                        <div className="flex-1">
                          <select
                            value={selectedTherapist}
                            onChange={(e) => setSelectedTherapist(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Therapist</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.full_name || user.email}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Meeting Type Filter */}
                        <div className="flex-1">
                          <select
                            value={selectedMeetingType}
                            onChange={(e) => setSelectedMeetingType(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Meeting Type</option>
                            {meetingTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="border-t border-gray-300">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-2 pl-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Date</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Meeting type</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Therapist</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Summary</th>
                              <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Apply filters
                              const filteredMeetings = meetings.filter((meeting) => {
                                if (selectedTherapist && meeting.therapist_id?.toString() !== selectedTherapist) {
                                  return false;
                                }
                                if (selectedMeetingType && meeting.meeting_type_id?.toString() !== selectedMeetingType) {
                                  return false;
                                }
                                return true;
                              });

                              if (filteredMeetings.length === 0) {
                                return (
                                  <tr>
                                    <td colSpan={4} className="py-8 text-center text-gray-500 text-sm">
                                      No meetings found
                                    </td>
                                  </tr>
                                );
                              }

                              return filteredMeetings.map((meeting, index) => {
                                // Get meeting type name
                                const meetingType = meetingTypes.find(mt => mt.id === meeting.meeting_type_id);
                                const meetingTypeName = meetingType?.name || '-';

                                // Get therapist name
                                const therapist = users.find(u => u.id === meeting.therapist_id);
                                const therapistName = therapist?.full_name || therapist?.email || '-';

                                // Format date and time (using user's local timezone for tooltip)
                                const meetingDate = formatDate(meeting.scheduled_at, userTimezone);
                                const meetingDateTime = formatDateTime(meeting.scheduled_at, {}, userTimezone);
                                // Calculate end time based on scheduled_at and duration_minutes
                                const startDateObj = parseDateAsUTC(meeting.scheduled_at);
                                const endDate = new Date(startDateObj.getTime() + (meeting.duration_minutes || 50) * 60000);
                                const meetingEndTime = formatTime(endDate, userTimezone);
                                const meetingTooltip = `${meetingDateTime} - ${meetingEndTime}`;

                                // Determine meeting category
                                const isCMMeeting = meeting.meeting_type?.type === 'case_manager_intake' || meeting.meeting_type?.type === 'case_manager_followup';
                                const isPSYMeeting = meeting.meeting_type?.type === 'psy_intake' || meeting.meeting_type?.type === 'psy_followup';
                                const isTitrationMeeting = meeting.meeting_type?.type === 'titration_intake' || meeting.meeting_type?.type === 'titration_followup';
                                const showSummaryIcon = meeting.status === 'done' && (
                                  ((isPSYMeeting || isTitrationMeeting) && (meeting.summary_ready || meeting.summary_sent)) ||
                                  (isCMMeeting && meeting.summary_approved)
                                );

                                const summaryValue = meeting.status === 'cancelled'
                                  ? 'Cancel'
                                  : meeting.status === 'noshow'
                                    ? 'No Show'
                                    : showSummaryIcon
                                      ? ''
                                      : meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1);

                                const summaryTooltip = meeting.summary_sent
                                  ? 'Sent'
                                  : meeting.summary_ready && (isPSYMeeting || isTitrationMeeting)
                                    ? 'Not Sent'
                                    : isCMMeeting && meeting.summary_approved
                                      ? 'Approved'
                                      : summaryValue;

                                return (
                                  <tr key={meeting.id} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                    <td className="py-2 pl-4 text-sm">
                                      <span
                                        className="inline-block px-2 py-1 rounded cursor-pointer hover:bg-gray-200 transition-colors text-gray-900"
                                        title={meetingTooltip}
                                        onClick={() => setSelectedMeetingId(meeting.id)}
                                      >
                                        {meetingDate}
                                      </span>
                                    </td>
                                    <td className="py-2 text-sm text-gray-900">{meetingTypeName}</td>
                                    <td className="py-2 text-sm text-gray-900">{therapistName}</td>
                                    <td className="py-2 text-sm">
                                      <span
                                        className={`inline-block px-2 py-1 rounded transition-colors ${(isCMMeeting || isPSYMeeting || isTitrationMeeting) ? 'cursor-pointer hover:bg-gray-200' : ''}`}
                                        title={summaryTooltip}
                                        onClick={() => {
                                          if (isCMMeeting || isTitrationMeeting) { setCmSummaryMeeting(meeting); setIsCMSummaryModalOpen(true); }
                                          else if (isPSYMeeting) { setDoctorSummaryMeeting(meeting); setIsDoctorSummaryModalOpen(true); }
                                        }}
                                      >
                                        {showSummaryIcon ? (
                                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                        ) : (
                                          <span className="text-gray-900">{summaryValue}</span>
                                        )}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Files Tab */}
                {activeTab === 'files' && (
                  <div className="flex flex-col">
                    {/* Header - Sticky */}
                    <div className="sticky top-[-0.5rem] bg-white z-10 pb-3 border-b border-gray-200 mb-1 -mx-1 px-1 pt-2">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold text-gray-900">Files</h3>
                        <div className="flex items-center gap-2">
                          {/* View Icon */}
                          <button
                            onClick={() => setIsViewFilesModalOpen(true)}
                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            title="View files"
                          >
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {/* Plus Icon */}
                          <button
                            onClick={() => setIsUploadFileModalOpen(true)}
                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                            title="Add file"
                          >
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Files List - Scrollable */}
                    <div className="flex-1 overflow-y-auto space-y-2">
                      {isLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-600 mt-2">Loading files...</p>
                        </div>
                      ) : files.filter((file) => file.description !== 'Meeting summary PDF').length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm">No files available</p>
                        </div>
                      ) : (
                        files.filter((file) => file.description !== 'Meeting summary PDF').map((file) => (
                          <div key={file.id} className="bg-[#d4e8e5] rounded-xl p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-gray-900 truncate">
                                  {file.file_type_name || file.file_type}
                                </span>
                                <span className="text-xs text-gray-600 ml-2 shrink-0">
                                  {formatDate(file.created_at, userTimezone)}
                                </span>
                              </div>
                              <div className="text-xs text-gray-700 truncate">
                                {file.file_name}{file.description ? ` - ${file.description}` : ''}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              {/* Edit icon */}
                              <button
                                onClick={() => handleEditFile(file)}
                                className="p-1.5 hover:bg-white/50 rounded transition-colors"
                                title="Edit file"
                              >
                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              {/* Download icon */}
                              <button
                                onClick={() => handleDownloadFile(file.id)}
                                className="p-1.5 hover:bg-white/50 rounded transition-colors"
                                title="Download file"
                              >
                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Questionnaires Tab */}
                {activeTab === 'questionnaires' && (
                  <div>
                    {/* Questionnaires Header */}
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                      <div className="px-4 py-3">
                        <h2 className="text-lg font-semibold text-gray-900">Questionnaires</h2>
                      </div>

                      {/* Questionnaires List */}
                      <div className="border-t border-gray-300 max-h-[calc(100vh-250px)] overflow-y-auto">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                              <p className="text-gray-600 mt-4 text-sm">Loading questionnaires...</p>
                            </div>
                          </div>
                        ) : questionnaires.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                            <p className="text-sm">No questionnaires available</p>
                          </div>
                        ) : (
                          <div className="px-4 py-3 space-y-3">
                            {questionnaires.map((questionnaire) => {
                              const isCompleted = !!questionnaire.finish_date;
                              // Times are stored UTC and rendered in the user's timezone —
                              // several submissions a day are otherwise indistinguishable.
                              const dateLabel = isCompleted
                                ? `Completed on: ${formatDateTime(questionnaire.finish_date!)}`
                                : questionnaire.sent_date
                                  ? `Sent on: ${formatDateTime(questionnaire.sent_date)}`
                                  : 'Not sent yet';

                              return (
                                <div
                                  key={questionnaire.id}
                                  className="bg-[#c8dfe3] rounded-lg px-4 py-3 flex items-center justify-between"
                                >
                                  <div className="flex-1">
                                    <h3 className="text-base font-semibold text-gray-900">
                                      {questionnaire.questionnaire_type?.title || 'Questionnaire'}
                                    </h3>
                                    <p className="text-sm text-gray-700">{dateLabel}</p>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {(questionnaire.finish_date || questionnaire.start_date) && (
                                      <button
                                        onClick={() => {
                                          setSelectedQuestionnaire(questionnaire);
                                          setIsViewQuestionnaireModalOpen(true);
                                        }}
                                        className="p-2 hover:bg-white/50 rounded transition-colors"
                                        title="View questionnaire"
                                      >
                                        <svg className="w-5 h-5 text-[#ff8c42]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Patient History Tab */}
                {activeTab === 'patient-history' && (
                  <div className="flex-1 flex flex-col">
                    {/* Rounded box container */}
                    <div className="flex-1 flex flex-col border border-gray-300 rounded-lg overflow-hidden">
                      {isLoading ? (
                        <div className="flex justify-center items-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                        </div>
                      ) : activityLogs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No patient history available</div>
                      ) : (
                        <div className="flex-1 flex flex-col overflow-hidden">
                          {/* Header Table - Fixed at top */}
                          <table className="w-full border-collapse flex-shrink-0">
                            <thead className="bg-gray-100">
                              <tr className="flex bg-gray-100">
                                <th
                                  className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                  style={patientHistoryColumnWidths['date'] ? { width: `${patientHistoryColumnWidths['date']}px`, minWidth: `${patientHistoryColumnWidths['date']}px`, maxWidth: `${patientHistoryColumnWidths['date']}px` } : { width: '150px' }}
                                  onClick={() => {
                                    if (patientHistorySortColumn === 'created_at') {
                                      setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setPatientHistorySortColumn('created_at');
                                      setPatientHistorySortDirection('desc');
                                    }
                                  }}
                                >
                                  Date {patientHistorySortColumn === 'created_at' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                  <div
                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'date', patientHistoryColumnWidths['date'] || 150)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                  />
                                </th>
                                <th
                                  className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                  style={patientHistoryColumnWidths['user'] ? { width: `${patientHistoryColumnWidths['user']}px`, minWidth: `${patientHistoryColumnWidths['user']}px`, maxWidth: `${patientHistoryColumnWidths['user']}px` } : { width: '150px' }}
                                  onClick={() => {
                                    if (patientHistorySortColumn === 'user_name') {
                                      setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setPatientHistorySortColumn('user_name');
                                      setPatientHistorySortDirection('asc');
                                    }
                                  }}
                                >
                                  User {patientHistorySortColumn === 'user_name' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                  <div
                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'user', patientHistoryColumnWidths['user'] || 150)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                  />
                                </th>
                                <th
                                  className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0"
                                  style={patientHistoryColumnWidths['type'] ? { width: `${patientHistoryColumnWidths['type']}px`, minWidth: `${patientHistoryColumnWidths['type']}px`, maxWidth: `${patientHistoryColumnWidths['type']}px` } : { width: '150px' }}
                                  onClick={() => {
                                    if (patientHistorySortColumn === 'activity_type') {
                                      setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setPatientHistorySortColumn('activity_type');
                                      setPatientHistorySortDirection('asc');
                                    }
                                  }}
                                >
                                  Type {patientHistorySortColumn === 'activity_type' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                  <div
                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'type', patientHistoryColumnWidths['type'] || 150)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                  />
                                </th>
                                <th
                                  className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-1"
                                  style={patientHistoryColumnWidths['action'] ? { width: `${patientHistoryColumnWidths['action']}px`, minWidth: `${patientHistoryColumnWidths['action']}px` } : { minWidth: '150px' }}
                                  onClick={() => {
                                    if (patientHistorySortColumn === 'description') {
                                      setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                    } else {
                                      setPatientHistorySortColumn('description');
                                      setPatientHistorySortDirection('asc');
                                    }
                                  }}
                                >
                                  Action {patientHistorySortColumn === 'description' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                  <div
                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'action', patientHistoryColumnWidths['action'] || 150)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                  />
                                </th>
                              </tr>
                            </thead>
                          </table>

                          {/* Body Table - Scrollable */}
                          <div className="flex-1 overflow-auto">
                            <table className="w-full border-collapse">
                              <tbody className="block">
                                {(() => {
                                  let sortedData = [...activityLogs];
                                  sortedData.sort((a, b) => {
                                    let aVal: any = a[patientHistorySortColumn as keyof typeof a];
                                    let bVal: any = b[patientHistorySortColumn as keyof typeof b];

                                    if (typeof aVal === 'string') {
                                      aVal = aVal.toLowerCase();
                                      bVal = (bVal as string).toLowerCase();
                                    }

                                    if (patientHistorySortDirection === 'asc') {
                                      return aVal > bVal ? 1 : -1;
                                    } else {
                                      return aVal < bVal ? 1 : -1;
                                    }
                                  });

                                  return sortedData.map((log) => (
                                    <tr key={log.id} className="flex hover:bg-gray-50">
                                      <td
                                        className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate"
                                        style={patientHistoryColumnWidths['date'] ? { width: `${patientHistoryColumnWidths['date']}px`, minWidth: `${patientHistoryColumnWidths['date']}px`, maxWidth: `${patientHistoryColumnWidths['date']}px` } : { width: '150px' }}
                                      >
                                        {formatDateTime(log.created_at, {}, userTimezone)}
                                      </td>
                                      <td
                                        className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate"
                                        style={patientHistoryColumnWidths['user'] ? { width: `${patientHistoryColumnWidths['user']}px`, minWidth: `${patientHistoryColumnWidths['user']}px`, maxWidth: `${patientHistoryColumnWidths['user']}px` } : { width: '150px' }}
                                      >
                                        {log.user_name || 'Unknown User'}
                                      </td>
                                      <td
                                        className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0"
                                        style={patientHistoryColumnWidths['type'] ? { width: `${patientHistoryColumnWidths['type']}px`, minWidth: `${patientHistoryColumnWidths['type']}px`, maxWidth: `${patientHistoryColumnWidths['type']}px` } : { width: '150px' }}
                                      >
                                        <span className="flex items-center gap-1">
                                          <span className="truncate">{log.activity_type.replace(/_/g, ' ').toUpperCase()}</span>
                                          {canDownloadPrompt && log.has_file && (
                                            <button
                                              title="Download summary data"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                  const url = await activityLogService.downloadPromptFile(log.id);
                                                  window.open(url, '_blank');
                                                } catch (_err) {
                                                  alert('Failed to download summary file.');
                                                }
                                              }}
                                              className="flex-shrink-0 text-blue-600 hover:text-blue-800"
                                            >
                                              ⬇
                                            </button>
                                          )}
                                        </span>
                                      </td>
                                      <td
                                        className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-1 truncate"
                                        style={patientHistoryColumnWidths['action'] ? { width: `${patientHistoryColumnWidths['action']}px`, minWidth: `${patientHistoryColumnWidths['action']}px` } : { minWidth: '150px' }}
                                      >
                                        {log.description}
                                      </td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prescription Tab (SignatureRx) */}
                {activeTab === 'prescription' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900">Prescriptions</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleOpenSignatureRx}
                          disabled={openingSignatureRx}
                          title="Create the patient in SignatureRx if needed and open SignatureRx for them"
                          className="px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {openingSignatureRx ? 'Opening…' : 'Open in SignatureRx'}
                        </button>
                        <button
                          onClick={loadPrescriptions}
                          disabled={prescriptionsLoading}
                          className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {prescriptionsLoading ? 'Refreshing…' : 'Refresh'}
                        </button>
                      </div>
                    </div>

                    {prescriptionsLoading && (
                      <p className="text-xs text-gray-500 italic">Loading prescriptions…</p>
                    )}

                    {!prescriptionsLoading && prescriptionsError && (
                      <div className="text-xs text-red-600">
                        {prescriptionsError}{' '}
                        <button onClick={loadPrescriptions} className="underline hover:text-red-700">
                          Retry
                        </button>
                      </div>
                    )}

                    {!prescriptionsLoading && !prescriptionsError && !prescriptionsLinked && (
                      <p className="text-xs text-gray-500 italic">
                        This patient is not linked to SignatureRx yet — use Open in SignatureRx above.
                      </p>
                    )}

                    {!prescriptionsLoading && !prescriptionsError && prescriptionsLinked && prescriptions.length === 0 && (
                      <p className="text-xs text-gray-500 italic">No prescriptions yet.</p>
                    )}

                    {!prescriptionsLoading && !prescriptionsError && prescriptions.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="text-gray-600">
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Date</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Medication name</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Dosage instruction</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Quantity</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Prescription notes</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Status</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">SignatureRx Rx ID</th>
                              <th className="border-b border-gray-200 px-2 py-1 text-left">Link</th>
                            </tr>
                          </thead>
                          <tbody>
                            {prescriptions.map((rx, idx) => (
                              <tr key={rx.signaturerx_prescription_id || idx} className="text-gray-900">
                                <td className="border-b border-gray-100 px-2 py-1 whitespace-nowrap">{formatPrescriptionDate(rx.date)}</td>
                                <td className="border-b border-gray-100 px-2 py-1">
                                  {rx.medication_name || '—'}
                                  {!!rx.medicines_count && rx.medicines_count > 1 && (
                                    <span className="text-gray-500"> +{rx.medicines_count - 1} more</span>
                                  )}
                                </td>
                                <td className="border-b border-gray-100 px-2 py-1">{rx.dosage_instruction || '—'}</td>
                                <td className="border-b border-gray-100 px-2 py-1">{rx.quantity || '—'}</td>
                                <td className="border-b border-gray-100 px-2 py-1">{rx.notes || '—'}</td>
                                <td className="border-b border-gray-100 px-2 py-1">{rx.status || '—'}</td>
                                <td className="border-b border-gray-100 px-2 py-1">{rx.signaturerx_prescription_id || '—'}</td>
                                <td className="border-b border-gray-100 px-2 py-1">
                                  {rx.url ? (
                                    <a href={rx.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">
                                      Open
                                    </a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Communications Tab */}
                {activeTab === 'communications' && (
                  <CommunicationsPanel
                    items={communications}
                    isLoading={commsLoading}
                    channelFilter={commChannelFilter}
                    directionFilter={commDirectionFilter}
                    onChannelFilter={setCommChannelFilter}
                    onDirectionFilter={setCommDirectionFilter}
                    onOpenMessage={setSelectedComm}
                    onCompose={() => setComposeOpen(true)}
                    canCompose={!!patient?.email}
                    userTimezone={userTimezone}
                  />
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {patient && (
        <>
          <ViewFilesModal
            isOpen={isViewFilesModalOpen}
            onClose={() => setIsViewFilesModalOpen(false)}
            files={files}
            onEditFile={handleEditFile}
            onDownloadFile={handleDownloadFile}
          />

          <UploadFileModal
            isOpen={isUploadFileModalOpen}
            onClose={() => setIsUploadFileModalOpen(false)}
            onUpload={handleUploadFile}
            fileTypes={fileTypes}
          />

          {selectedFile && (
            <EditFileModal
              isOpen={isEditFileModalOpen}
              onClose={() => {
                setIsEditFileModalOpen(false);
                setSelectedFile(null);
              }}
              file={selectedFile}
              onSave={handleSaveFileEdit}
              fileTypes={fileTypes}
            />
          )}

          {selectedQuestionnaire && (
            <ViewQuestionnaireModal
              isOpen={isViewQuestionnaireModalOpen}
              onClose={() => {
                setIsViewQuestionnaireModalOpen(false);
                setSelectedQuestionnaire(null);
              }}
              questionnaireId={selectedQuestionnaire.id}
              questionnaireTitle={selectedQuestionnaire.questionnaire_type?.title || 'Questionnaire'}
            />
          )}

          <ViewMeetingsModal
            isOpen={isViewMeetingsModalOpen}
            onClose={() => setIsViewMeetingsModalOpen(false)}
            patientId={patient.id}
            patientName={patient.full_name}
            onAction={handleMeetingAction}
            refreshKey={meetingsRefreshKey}
          />

          {selectedMeetingForAction && (
            <>
              <NoShowModal
                isOpen={isNoShowModalOpen}
                onClose={() => { setIsNoShowModalOpen(false); setSelectedMeetingForAction(null); }}
                meetingId={selectedMeetingForAction.id}
                onSuccess={() => { setIsNoShowModalOpen(false); setSelectedMeetingForAction(null); setMeetingsRefreshKey(k => k + 1); if (currentTenant && patient) meetingService.getMeetings(currentTenant.id, patient.id).then(setMeetings); }}
              />
              <CancelMeetingModal
                isOpen={isCancelMeetingModalOpen}
                onClose={() => { setIsCancelMeetingModalOpen(false); setSelectedMeetingForAction(null); }}
                meetingId={selectedMeetingForAction.id}
                onSuccess={() => { setIsCancelMeetingModalOpen(false); setSelectedMeetingForAction(null); setMeetingsRefreshKey(k => k + 1); }}
              />
              <FinishTreatmentModal
                isOpen={isFinishTreatmentModalOpen}
                onClose={() => { setIsFinishTreatmentModalOpen(false); setSelectedMeetingForAction(null); }}
                patientId={selectedMeetingForAction.patient_id}
                patientName={selectedMeetingForAction.patient?.full_name || ''}
                onSuccess={() => { setIsFinishTreatmentModalOpen(false); setSelectedMeetingForAction(null); setMeetingsRefreshKey(k => k + 1); }}
              />
              <EditCommentsModal
                isOpen={isEditMeetingCommentsModalOpen}
                onClose={() => { setIsEditMeetingCommentsModalOpen(false); setSelectedMeetingForAction(null); }}
                initialNotes={selectedMeetingForAction.notes || ''}
                onSave={async (newNotes) => {
                  await meetingService.updateMeeting(selectedMeetingForAction.id, { notes: newNotes });
                  setIsEditMeetingCommentsModalOpen(false);
                  setSelectedMeetingForAction(null);
                  setMeetingsRefreshKey(k => k + 1);
                }}
                title="Edit Meeting Comments"
              />
              <EmailSendDialog
                open={isMeetingEmailDialogOpen}
                onClose={() => { setIsMeetingEmailDialogOpen(false); setSelectedMeetingForAction(null); }}
                meetingId={selectedMeetingForAction.id}
                patientName={selectedMeetingForAction.patient?.full_name || ''}
                patientEmail={selectedMeetingForAction.patient?.email || null}
              />
              <SmsSendDialog
                open={isMeetingSmsDialogOpen}
                onClose={() => { setIsMeetingSmsDialogOpen(false); setSelectedMeetingForAction(null); }}
                meetingId={selectedMeetingForAction.id}
                patientName={selectedMeetingForAction.patient?.full_name || ''}
                patientPhone={selectedMeetingForAction.patient?.phone ?? null}
              />
              <CallPatientDialog
                open={isMeetingCallDialogOpen}
                onClose={() => { setIsMeetingCallDialogOpen(false); setSelectedMeetingForAction(null); }}
                patientId={selectedMeetingForAction.patient_id}
                meetingId={selectedMeetingForAction.id}
                patientName={selectedMeetingForAction.patient?.full_name || ''}
                patientPhone={selectedMeetingForAction.patient?.phone ?? null}
                onSuccess={(message) => toast.success(message)}
                onError={(message) => toast.error(message)}
              />
            </>
          )}

          <EditCommentsModal
            isOpen={isEditNotesModalOpen}
            onClose={() => setIsEditNotesModalOpen(false)}
            initialNotes={patient.notes || ''}
            onSave={handleSaveNotes}
            isSaving={isSavingNotes}
          />

          <CallPatientDialog
            open={isPatientCallDialogOpen}
            onClose={() => setIsPatientCallDialogOpen(false)}
            patientId={patient.id}
            patientName={patient.full_name}
            patientPhone={patient.phone ?? null}
            onSuccess={(message) => toast.success(message)}
            onError={(message) => toast.error(message)}
          />

          <ComposeEmailDialog
            open={composeOpen}
            onClose={() => setComposeOpen(false)}
            patientId={patient.id}
            patientName={patient.full_name}
            patientEmail={patient.email ?? null}
            onSent={() => { setComposeOpen(false); loadCommunications(); }}
          />

          <CommunicationDetailModal
            comm={selectedComm}
            onClose={() => setSelectedComm(null)}
            userTimezone={userTimezone}
          />

          {createTaskOpen && (
            <InternalTaskModal
              open
              onClose={() => setCreateTaskOpen(false)}
              onSaved={loadPatientTasks}
              patientId={patient.id}
              onOpenEditPatient={handleEditPatient}
              onOpenNewMeeting={() => setIsYomanDialogOpen(true)}
              onOpenSummary={(m) => {
                if (usesSingleTextSummary(m)) { setCmSummaryMeeting(m); setIsCMSummaryModalOpen(true); }
                else { setDoctorSummaryMeeting(m); setIsDoctorSummaryModalOpen(true); }
              }}
            />
          )}
          {editTask && (
            <InternalTaskModal
              open
              onClose={() => setEditTask(null)}
              onSaved={loadPatientTasks}
              patientId={patient.id}
              task={editTask}
              onOpenEditPatient={handleEditPatient}
              onOpenNewMeeting={() => setIsYomanDialogOpen(true)}
              onOpenSummary={(m) => {
                if (usesSingleTextSummary(m)) { setCmSummaryMeeting(m); setIsCMSummaryModalOpen(true); }
                else { setDoctorSummaryMeeting(m); setIsDoctorSummaryModalOpen(true); }
              }}
            />
          )}

          <UploadTranscriptModal
            isOpen={isUploadTranscriptModalOpen}
            onClose={() => {
              setIsUploadTranscriptModalOpen(false);
              setSelectedMeetingForAction(null);
            }}
            meetings={meetings}
            onUpload={handleUploadTranscript}
            isUploading={isUploadingTranscript}
            preSelectedMeetingId={selectedMeetingForAction?.id}
            disableMeetingSelection={!!selectedMeetingForAction}
          />

          <YomanSchedulingDialog
            open={isYomanDialogOpen}
            onClose={handleYomanDialogClose}
            patient={patient}
            meetingTypes={meetingTypes}
            loading={yomanLoading}
            currentTenantId={currentTenant?.id}
            onCreateInvite={handleYomanCreateInvite}
          />

          <PatientFormDialog
            open={isEditPatientModalOpen}
            onClose={() => { setIsEditPatientModalOpen(false); setPatientFieldErrors({}); }}
            onSubmit={handleFormSubmit(handleSavePatient)}
            editingPatient={patient}
            register={register}
            errors={errors}
            submitCount={submitCount}
            control={control}
            defaultCountryCode={defaultCountryCode}
            clinicState={clinicState}
            fieldErrors={patientFieldErrors}
            onClearFieldError={(field) => setPatientFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; })}
            patientTypeId={patient.patient_type_id ?? undefined}
          />
        </>
      )}

      {cmSummaryMeeting && (
        <CMSummaryModal
          isOpen={isCMSummaryModalOpen}
          onClose={() => { setIsCMSummaryModalOpen(false); setCmSummaryMeeting(null); }}
          meeting={cmSummaryMeeting}
          onSuccess={() => setMeetingsRefreshKey(k => k + 1)}
        />
      )}
      {doctorSummaryMeeting && (
        <DoctorSummaryModal
          isOpen={isDoctorSummaryModalOpen}
          onClose={() => { setIsDoctorSummaryModalOpen(false); setDoctorSummaryMeeting(null); }}
          meeting={doctorSummaryMeeting}
          onSuccess={() => setMeetingsRefreshKey(k => k + 1)}
          clinicLanguage={clinics.find(c => c.id === patient?.clinic_id)?.language}
          clinicState={clinicState}
        />
      )}
    </div>
  );
};

export default PatientDetailPage;
